import { useMemo, useRef, useState, useId } from 'react';
import {
  FIELD_W,
  FIELD_H,
  FOCAL,
  C,
  CONTOURS,
  BROAD_PLANES,
  radarMembranePath,
  radarRelief,
  RADAR_R,
  RADAR_ANGLES,
  RADAR_PROFILE,
  RADAR_PHASE,
  DOMAIN_LABELS,
  DOMAIN_FULL,
} from './analyticalFieldGeometry';
import { useScrollParallax } from '@/hooks/useScrollParallax';
import { useAmbientMotion } from '@/hooks/useAmbientMotion';

const REDUCED = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const INTENSITY = { hero: 1, page: 0.35, quiet: 0.15, substrate: 0.3, none: 0 };

/**
 * OnPoint "Analytical Field" — a reusable analytical-terrain background.
 * One shared deterministic SVG grammar at multiple intensities:
 *   variant="hero"      — richest: contour field + 5-domain radar + scroll parallax
 *   variant="page"      — same contour language, low opacity, no radar, idle motion
 *   variant="quiet"     — near-atmospheric ruling/contour, no radar, no motion
 *   variant="substrate" — very faint, top-anchored terrain (extends behind the header),
 *                         no radar, no motion, fades downward into the hero
 * Every SVG def (gradients/masks) is namespaced per instance so multiple fields can
 * coexist on one page (substrate + hero) without id collisions.
 */
export function AnalyticalField({
  variant = 'hero',
  focalPosition,
  intensity,
  motion,
  showRadar,
  className = '',
  ariaLabel = 'Analytical terrain field',
}) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const idn = (n) => `af-${n}-${uid}`;

  const inten = intensity ?? INTENSITY[variant] ?? 1;
  const isSubstrate = variant === 'substrate';
  const withRadar = showRadar ?? variant === 'hero';
  const withMotion = motion ?? (variant === 'hero' ? 'scroll' : variant === 'page' ? 'idle' : 'none');
  const withAmbient = variant === 'hero' || variant === 'page';
  const [reduced] = useState(REDUCED);
  const rootRef = useRef(null);
  useScrollParallax(rootRef, withMotion === 'scroll' && !reduced);
  useAmbientMotion(rootRef, withAmbient && !reduced);

  const fx = 1200 * (focalPosition ? parseFloat(String(focalPosition).split(',')[0]) : 0.72);
  const fy = 800 * (focalPosition ? parseFloat(String(focalPosition).split(',')[1]) : 0.5);

  const radar = useMemo(() => {
    if (!withRadar) return null;
    const cx = fx;
    const cy = fy;
    const R = RADAR_R;
    // THE single source: an 8-vertex membrane passing exactly through the 8 value points.
    // The same vertex set drives the primary silhouette, coral perimeter, primary nodes,
    // and every nested relief level — one analytical object, not organics + overlay.
    const primary = radarMembranePath(cx, cy, R, RADAR_PROFILE, RADAR_PHASE);
    // ghost baseline (expected/reference) — same 8-axis structure, offset, dashed
    const baseline = radarMembranePath(cx, cy, R * 0.97, RADAR_PROFILE, RADAR_PHASE + 0.55);
    // dashed echo ring just outside the primary perimeter
    const echo = radarMembranePath(cx, cy, R * 1.04, RADAR_PROFILE, RADAR_PHASE + 1.1);
    // nested relief — 16 interpolations of the SAME 8-value profile (center → perimeter)
    const relief = radarRelief(cx, cy, R, RADAR_PROFILE, RADAR_PHASE, 16);
    // 8 radial axis spokes — exactly to the 8 value points
    const spokes = RADAR_ANGLES.map((a, k) => [cx, cy, cx + R * RADAR_PROFILE[k] * Math.cos(a), cy + R * RADAR_PROFILE[k] * Math.sin(a)]);
    // nodes sit ON the primary membrane = the 8 value points
    const nodes = RADAR_ANGLES.map((a, k) => [cx + R * RADAR_PROFILE[k] * Math.cos(a), cy + R * RADAR_PROFILE[k] * Math.sin(a)]);
    const primaryNodes = nodes;
    // ghost baseline nodes
    const baselineNodes = RADAR_ANGLES.map((a, k) => [cx + R * 0.97 * RADAR_PROFILE[k] * Math.cos(a), cy + R * 0.97 * RADAR_PROFILE[k] * Math.sin(a)]);
    // label anchors — slightly closer to the axis endpoints (integrated, not detached)
    const labelR = R * 1.14;
    const labels = RADAR_ANGLES.map((a, k) => [cx + labelR * RADAR_PROFILE[k] * Math.cos(a), cy + labelR * RADAR_PROFILE[k] * Math.sin(a)]);
    // light crest along the upper rim (Demand & Forecast → Coverage → Schedule)
    const crest = `M${nodes[7][0].toFixed(1)} ${nodes[7][1].toFixed(1)} L${nodes[0][0].toFixed(1)} ${nodes[0][1].toFixed(1)} L${nodes[1][0].toFixed(1)} ${nodes[1][1].toFixed(1)}`;
    const tickR = R * 0.22;
    // 8 facet wedges — center→node sectors, one per domain. Each is a broad fold that
    // widens toward its domain vertex (the membrane "stretched over a radial rib").
    const facets = RADAR_ANGLES.map((a, k) => {
      const aPrev = RADAR_ANGLES[(k + 7) % 8];
      const aNext = RADAR_ANGLES[(k + 1) % 8];
      // wedge corners: hub start at center, out to the domain node, via mid-shoulder points
      const hub = 0;
      const innerMid = RADAR_PROFILE[k] * 0.5;
      const p0 = [cx + R * hub * Math.cos(a), cy + R * hub * Math.sin(a)];
      const pL = [cx + R * innerMid * Math.cos(aPrev), cy + R * innerMid * Math.sin(aPrev)];
      const pR = [cx + R * innerMid * Math.cos(aNext), cy + R * innerMid * Math.sin(aNext)];
      const pNode = [cx + R * RADAR_PROFILE[k] * Math.cos(a), cy + R * RADAR_PROFILE[k] * Math.sin(a)];
      return {
        d: `M${p0[0].toFixed(1)} ${p0[1].toFixed(1)} ` +
          `C${pL[0].toFixed(1)} ${pL[1].toFixed(1)} ${((pL[0] + pNode[0]) / 2).toFixed(1)} ${((pL[1] + pNode[1]) / 2).toFixed(1)} ${pNode[0].toFixed(1)} ${pNode[1].toFixed(1)} ` +
          `C${((pR[0] + pNode[0]) / 2).toFixed(1)} ${((pR[1] + pNode[1]) / 2).toFixed(1)} ${pR[0].toFixed(1)} ${pR[1].toFixed(1)} ${p0[0].toFixed(1)} ${p0[1].toFixed(1)}Z`,
      };
    });
    return { cx, cy, baseline, primary, echo, spokes, nodes, primaryNodes, baselineNodes, labels, crest, relief, facets, tickR };
  }, [withRadar, fx, fy]);

  const labelAnchor = (k) => {
    const cos = Math.cos(RADAR_ANGLES[k]);
    if (Math.abs(cos) < 0.3) return 'middle';
    return cos > 0 ? 'start' : 'end';
  };
  const labelDy = (k) => Math.round(Math.sin(RADAR_ANGLES[k]) * 6) - 7; // -7 to centre the two-line block (labels raised)

  // Contour hierarchy — 3 restrained visual tiers by radial proximity to the radar
  // (contour radii are index-ordered: i=0 closest → i=7 farthest). Only the stroke /
  // opacity treatment changes; the organic contour geometry is untouched.
  const contourTier = (i) => {
    if (i <= 2) return { stroke: C.ink2, op: 0.3, w: 1.0 }; // MAJOR — warm graphite, near radar
    if (i <= 4) return { stroke: C.sage, op: 0.2, w: 0.6 }; // MINOR — sage, mid field
    return { stroke: C.sageLight, op: 0.11, w: 0.5 }; // GHOST — faint, dissolves into terrain
  };

  // Two-line labels: split "X & Y" → "X &" / "Y" so the perimeter labels stay compact
  // (halves their horizontal extent; no clipping at the field's right edge).
  const splitLabel = (s) => {
    const i = s.indexOf(' & ');
    return i > 0 ? [s.slice(0, i) + ' &', s.slice(i + 3)] : [s];
  };

  // Substrate uses a single downward fade (visible at the top, dissolves toward the hero);
  // hero/page/quiet use the top fade-in + bottom fade-out pair.
  const topMask = isSubstrate ? undefined : `url(#${idn('mask-top')})`;
  const outerMask = isSubstrate ? `url(#${idn('mask-substrate')})` : `url(#${idn('mask-bottom')})`;
  // Phase 1B — hero only: a soft left ramp over the decorative terrain/contour material
  // under the editorial copy (cleaner warm paper behind the headline; radar untouched).
  const copyMask = variant === 'hero' ? `url(#${idn('mask-left')})` : undefined;

  return (
    <svg
      ref={rootRef}
      className={`analytical-field pointer-events-none absolute inset-0 h-full w-full ${className}`}
      viewBox={`0 0 ${FIELD_W} ${FIELD_H}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
      style={{ opacity: inten }}
    >
      <defs>
        <radialGradient id={idn('wash-a')} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={C.sageLight} stopOpacity="0.09" />
          <stop offset="70%" stopColor={C.sage} stopOpacity="0.06" />
          <stop offset="100%" stopColor={C.sage} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={idn('wash-b')} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={C.green} stopOpacity="0.07" />
          <stop offset="70%" stopColor={C.green} stopOpacity="0.05" />
          <stop offset="100%" stopColor={C.greenDeep} stopOpacity="0" />
        </radialGradient>
        {/* soft left ramp — decorative material under the editorial copy keeps ~82%
            presence at the far-left, easing to full strength by mid-field; never hits
            black, so the full-field bleed and terrain continuity stay visible */}
        <linearGradient id={idn('fade-left')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#d0d0d0" />
          <stop offset="0.5" stopColor="#fff" />
          <stop offset="1" stopColor="#fff" />
        </linearGradient>
        <linearGradient id={idn('fade-top')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#000" />
          <stop offset="0.22" stopColor="#fff" />
          <stop offset="1" stopColor="#fff" />
        </linearGradient>
        <linearGradient id={idn('fade-bottom')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" />
          <stop offset="0.82" stopColor="#fff" />
          <stop offset="1" stopColor="#000" />
        </linearGradient>
        <linearGradient id={idn('fade-substrate')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" />
          <stop offset="0.35" stopColor="#fff" />
          <stop offset="0.85" stopColor="#000" />
          <stop offset="1" stopColor="#000" />
        </linearGradient>
        <mask id={idn('mask-left')}>
          <rect width={FIELD_W} height={FIELD_H} fill={`url(#${idn('fade-left')})`} />
        </mask>
        <mask id={idn('mask-top')}>
          <rect width={FIELD_W} height={FIELD_H} fill={`url(#${idn('fade-top')})`} />
        </mask>
        <mask id={idn('mask-bottom')}>
          <rect width={FIELD_W} height={FIELD_H} fill={`url(#${idn('fade-bottom')})`} />
        </mask>
        <mask id={idn('mask-substrate')}>
          <rect width={FIELD_W} height={FIELD_H} fill={`url(#${idn('fade-substrate')})`} />
        </mask>
      </defs>

      <g className="af-field">
        <g mask={topMask}>
          <g mask={outerMask}>
            {/* condition wash — soft depth, no glow */}
            <ellipse cx={fx} cy={fy} rx="440" ry="330" fill={`url(#${idn('wash-a')})`} />
            <ellipse cx={fx - 110} cy={fy + 80} rx="360" ry="300" fill={`url(#${idn('wash-b')})`} />

            {/* broad soft dimensional terrain planes — large overlapping sage surfaces
                (the authored analytical landscape the radar emerges from) */}
            <g className="af-planes" mask={copyMask}>
              {BROAD_PLANES.map((p, i) => {
                // kind → fill: deep = darker sage, broad = pale sage sheet,
                // crest = light highlight, edge = sage plane w/ faint coral line
                const fill = p.kind === 'deep' ? C.green
                  : p.kind === 'crest' ? C.cream
                  : C.sageLight;
                const base = p.op ?? (p.kind === 'broad' ? 0.11 : 0.07);
                // Phase 1B — outer-sage restraint: far/background sheets (deep foundation,
                // broad sweeps, distant coral-edge plane) pull ~12% quieter; the diagonal
                // crest flowing toward the radar stays at full strength (radar support).
                const op = p.kind === 'crest' ? base : base * 0.88;
                return (
                  <g key={`bp-${i}`}>
                    <path d={p.d} fill={fill} fillOpacity={op} />
                    {p.kind === 'edge' && (
                      <path d={p.d} fill="none" stroke={C.coral} strokeOpacity="0.22"
                        strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
                    )}
                  </g>
                );
              })}
            </g>

            {/* fine contour hairlines over the terrain — tiered MAJOR / MINOR / GHOST */}
            <g className="af-conts" mask={copyMask}>
              {CONTOURS.map((r) => {
                const t = contourTier(r.i);
                return (
                  <path key={`c-${r.i}`} d={r.d}
                    fill="none" stroke={t.stroke} strokeOpacity={t.op} strokeWidth={t.w} vectorEffect="non-scaling-stroke" />
                );
              })}
            </g>

            {/*
            engraved echo hairlines over the terrain (left-faded) — offset copy of the
            fine contours reading as an engraved engraving, quieting toward the left.
            */}
            <g className="af-rings" mask={`url(#${idn('mask-left')})`}>
              {CONTOURS.map((r) => {
                const t = contourTier(r.i);
                return (
                  <g key={`ce-${r.i}`}>
                    <path d={r.d} transform="translate(1.2 1.2)"
                      fill="none" stroke={C.ink2} strokeOpacity={(t.op * 0.5).toFixed(3)}
                      strokeWidth={t.w} vectorEffect="non-scaling-stroke" />
                    <path d={r.d}
                      fill="none" stroke={t.stroke} strokeOpacity={(t.op * 0.4).toFixed(3)}
                      strokeWidth={t.w} vectorEffect="non-scaling-stroke" />
                  </g>
                );
              })}
            </g>

            {/* ── 8-axis radar (hero focal object) — dimensional topographic membrane ── */}
            {radar && (
              <g className="af-plane">
                <g className="af-tilt" data-cx={radar.cx} data-cy={radar.cy}>
                  <g transform={`translate(${radar.cx} ${radar.cy}) rotate(-4) scale(1 0.855) translate(${-radar.cx} ${-radar.cy})`}>
                    {/* ambient-motion group (inner) — relief breathing, no spinning */}
                    <g className="af-ambient" data-cx={radar.cx} data-cy={radar.cy}>

                      {/* layer 1: extrusion shadow (cast down-right) — object has thickness */}
                      <g className="af-shadow">
                        <path d={radar.primary} transform="translate(6 8)" fill={C.ink2} fillOpacity="0.045" />
                      </g>

                      {/* layer 2: ghost baseline membrane — the expected/reference structure,
                          same 8-axis profile, offset, dashed, with its own subtle nodes */}
                      <g className="af-baseline">
                        <g transform="translate(4 6)">
                          <path d={radar.baseline} fill={C.sage} fillOpacity="0.05"
                            stroke={C.greenDeep} strokeOpacity="0.2" strokeWidth="1" strokeDasharray="1.5 2.6" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                          {radar.baselineNodes.map(([x, y], k) => (
                            <circle key={`bn-${k}`} cx={x} cy={y} r="1.15" fill={C.inkMid} fillOpacity="0.85" />
                          ))}
                        </g>
                      </g>

                      {/* layer 3: lower translucent membrane (whisper of fill, same vertices) */}
                      <g className="af-lower">
                        <path d={radar.primary} transform="translate(3 3)" fill={C.sage} fillOpacity="0.05" />
                        <path d={radar.primary} transform="translate(4.5 5)" fill={C.green} fillOpacity="0.04" />
                      </g>

                      {/* layer 4: 8 fine radial axis spokes — exactly to the 8 value points */}
                      <g className="af-axes">
                        {radar.spokes.map(([x1, y1, x2, y2], k) => (
                          <line key={`ax-${k}`} x1={x1} y1={y1} x2={x2} y2={y2}
                            stroke={C.ink2} strokeOpacity="0.17" strokeWidth="0.9" vectorEffect="non-scaling-stroke" />
                        ))}
                      </g>

                      {/* layer 5: DIRECTIONAL 3D RELIEF — 16 height-field contour surfaces warped by the
                          8 domain values. Lighting is asymmetric: a cream ridge toward the
                          upper-right, a darker sage basin toward lower-center, soft shadow
                          falloff beneath the central surface. Contours compress near steep
                          transitions and relax on flats (no concentric rings). */}
                      <g className="af-relief">
                        {/* translucent light/dark fans between the nested surfaces */}
                        {radar.relief.map((r, i) => {
                          // asymmetric exposure from the height-field (0..1)
                          const L = r.light;
                          // bright interval → cream ridge; dim interval → dark sage basin;
                          // stronger band opacity gives the centre visible sculptural presence
                          const band = L > 0.54 ? C.cream : L < 0.40 ? C.green : C.sageLight;
                          const bandOp = 0.045 + L * 0.07;
                          // RADAR TONAL AUTHORITY: the 3–5 relief bands immediately around the
                          // perimeter deepen ~one palette step toward greenDeep, decaying
                          // organically inward as a smooth function of the band spacing (f) —
                          // no closed dark region, no vignette, no hard edge; outer terrain stays quiet.
                          const lift = Math.max(0, (r.f - 0.66) / 0.34);
                          return (
                            <g key={`rb-${i}`}>
                              <path d={r.d} fill={band} fillOpacity={bandOp.toFixed(3)} />
                              {lift > 0 && (
                                <path d={r.d} fill={C.greenDeep}
                                  fillOpacity={(0.016 + lift * 0.055).toFixed(3)} />
                              )}
                            </g>
                          );
                        })}
                        {/* 8 center→node facet wedges — subtle wedge-to-wedge tonal change
                            so the membrane reads as stretched over radial ribs (no hard
                            triangles). Brighter toward high-value domains (the directional
                            tension the 8 axes create). */}
                        {radar.facets.map((fd, k) => (
                          <path key={`fc-${k}`} d={fd.d}
                            fill={k % 2 === 0 ? C.sageLight : C.cream}
                            fillOpacity={(0.032 + RADAR_PROFILE[k] * 0.05).toFixed(3)} />
                        ))}
                        {/* fine height-field tension lines — compressed near steep transitions */ }
                        {radar.relief.map((r, i) => {
                          // embossed pair: a dark under-edge (offset +0.8) + a light
                          // over-edge (offset −0.8) around the main stroke give each ring
                          // a shallow raised/elevated reading — geometry unchanged.
                          const main = i % 2 === 0 ? C.green : C.sageLight;
                          const opMain = (0.08 + r.light * 0.16).toFixed(3);
                          return (
                            <g key={`rl-${i}`}>
                              <path d={r.d} transform="translate(0.8 0.8)" fill="none"
                                stroke={C.green} strokeOpacity={(parseFloat(opMain) * 0.7).toFixed(3)}
                                strokeWidth={i % 2 === 0 ? 0.9 : 1.1} vectorEffect="non-scaling-stroke" />
                              <path d={r.d} transform="translate(-0.8 -0.8)" fill="none"
                                stroke={C.cream} strokeOpacity={(parseFloat(opMain) * 0.55).toFixed(3)}
                                strokeWidth={i % 2 === 0 ? 0.8 : 1} vectorEffect="non-scaling-stroke" />
                              <path d={r.d} fill="none" transform="translate(-0.2 -0.2)"
                                stroke={r.light > 0.5 ? C.cream : main}
                                strokeOpacity={opMain}
                                strokeWidth={i % 2 === 0 ? 0.8 : 1} vectorEffect="non-scaling-stroke" />
                            </g>
                          );
                        })}
                      </g>

                      {/* layer 6: dashed echo ring just outside the 8-value perimeter */}
                      <g className="af-echo">
                        <path d={radar.echo} fill="none"
                          stroke={C.green} strokeOpacity="0.13" strokeWidth="0.9"
                          strokeDasharray="1.6 5" vectorEffect="non-scaling-stroke" />
                      </g>

                      {/* layer 7: primary 8-axis membrane — STACKED copies create apparent Z rise.
                          Back shadow → lower membrane → mid → primary → highlight skin,
                          each exactly the same 8-axis geometry, only translated for depth. */}
                      <g className="af-primary">
                        {/* deep back shadow (furthest down-right) — raises the object */}
                        <path d={radar.primary} transform="translate(6 7)" fill={C.green} fillOpacity="0.05" />
                        <path d={radar.primary} transform="translate(4.5 5.5)" fill={C.green} fillOpacity="0.06" />
                        {/* darker underside (offset down-right) — soft local shadow falloff */}
                        <path d={radar.primary} transform="translate(3 3.5)" fill={C.green} fillOpacity="0.07" />
                        {/* main translucent sage membrane (lifted slightly for radar authority) */}
                        <path d={radar.primary} fill={C.sage} fillOpacity="0.08" />
                        {/* pale highlight skin — brighter on the upper-left exposure */}
                        <path d={radar.primary} fill={C.cream} fillOpacity="0.09" />
                        {/* raised highlight ledge (tiny offset up-left) — tops the object */}
                        <path d={radar.primary} transform="translate(-1.2 -1.4)" fill="none"
                          stroke={C.cream} strokeOpacity="0.30" strokeWidth="1" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                        {/* graphite structure edge */}
                        <path d={radar.primary} fill="none"
                          stroke={C.green} strokeOpacity="0.32" strokeWidth="0.9" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                        {/* upper-left highlight hairline (soft relief ridge) */}
                        <path d={radar.primary} transform="translate(-0.9 -0.9)" fill="none"
                          stroke={C.cream} strokeOpacity="0.4" strokeWidth="0.9" vectorEffect="non-scaling-stroke" />
                        {/* directional soft shade patch (lower-right) — very subtle volume */}
                        <path d={radar.primary} transform="translate(1.4 2.2)" fill="none"
                          stroke={C.green} strokeOpacity="0.10" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
                      </g>

                      {/* layer 8: thin coral current-state outline — the single restrained
                          signal passing EXACTLY through the 8 value nodes (same path).
                          Under it a restrained structural shadow edge lifts the perimeter. */}
                      <g className="af-rim">
                        {/* structural depth edge — displaced sage shadow under the coral,
                            1–2px, low opacity (suspends the perimeter above lower terrain) */}
                        <path d={radar.primary} transform="translate(1.4 1.8)" fill="none"
                          stroke={C.green} strokeOpacity="0.18" strokeWidth="1.1" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                        <path className="af-rimline" data-cx={radar.cx} data-cy={radar.cy}
                          d={radar.primary} fill="none" stroke={C.coral} strokeOpacity="0.6"
                          strokeWidth="1.3" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                        {/* crest highlight along the upper rim */}
                        <path d={radar.crest} fill="none" stroke={C.cream} strokeOpacity="0.3" strokeWidth="1.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                      </g>

                      {/* layer 9: central convergence hub — a soft crosshair + small node.
                          Give the rings a shallow bowl/emboss: a tiny cream highlight on the
                          upper edge and a soft sage shadow on the lower edge of the same ring
                          (geometry unaltered; reads as a slightly recessed analytical origin). */}
                      <g className="af-hub" data-cx={radar.cx} data-cy={radar.cy}>
                        {/* embossed central ring — highlight upper / shadow lower */}
                        <circle cx={radar.cx - 0.7} cy={radar.cy - 0.7} r={radar.tickR} fill="none"
                          stroke={C.cream} strokeOpacity="0.16" strokeWidth="0.7" vectorEffect="non-scaling-stroke" />
                        <circle cx={radar.cx + 0.7} cy={radar.cy + 0.7} r={radar.tickR} fill="none"
                          stroke={C.green} strokeOpacity="0.14" strokeWidth="0.7" vectorEffect="non-scaling-stroke" />
                        <circle cx={radar.cx} cy={radar.cy} r={radar.tickR} fill="none"
                          stroke={C.ink2} strokeOpacity="0.14" strokeWidth="0.7" vectorEffect="non-scaling-stroke" />
                        <line x1={radar.cx - radar.tickR - 4} y1={radar.cy} x2={radar.cx + radar.tickR + 4} y2={radar.cy}
                          stroke={C.ink2} strokeOpacity="0.1" strokeWidth="0.6" />
                        <line x1={radar.cx} y1={radar.cy - radar.tickR - 4} x2={radar.cx} y2={radar.cy + radar.tickR + 4}
                          stroke={C.ink2} strokeOpacity="0.1" strokeWidth="0.6" />
                        <circle cx={radar.cx} cy={radar.cy} r="1.4" fill={C.greenDeep} fillOpacity="0.9" />
                      </g>

                      {/* layer 10: nodes sit precisely ON the coral 8-value perimeter + editorial
                          halo-free labels nestled in close to the axis endpoints */}
                      <g className="af-nodes">
                        {radar.primaryNodes.map(([x, y], k) => (
                          <g key={`pn-${k}`} className="af-node">
                            {/* tiny shadow under the node (down-right) */}
                            <circle cx={x + 0.6} cy={y + 0.6} r="1.9" fill={C.green} fillOpacity="0.30" />
                            {/* dark center (the node itself) */}
                            <circle cx={x} cy={y} r="1.9" fill={C.greenDeep} fillOpacity="0.95"
                              stroke={C.coral} strokeOpacity="0.35" strokeWidth="0.7" />
                            {/* tiny cream highlight (up-left) */}
                            <circle cx={x - 0.6} cy={y - 0.6} r="0.7" fill={C.cream} fillOpacity="0.7" />
                          </g>
                        ))}
                        {radar.labels.map(([x, y], k) => (
                          <text key={`lb-${k}`} x={x.toFixed(1)} y={(y + labelDy(k)).toFixed(1)}
                            textAnchor={labelAnchor(k)} fontFamily="var(--font-display)" fontSize="14.5"
                            letterSpacing="0.012em" fill={C.inkMid} fontStyle="italic"
                            opacity="1">
                            {splitLabel(DOMAIN_LABELS[k]).map((l, li) => (
                              <tspan key={li} x={x.toFixed(1)} dy={li === 0 ? 0 : 17.3}>{l}</tspan>
                            ))}
                          </text>
                        ))}
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            )}

            {/* survey ticks (wordless "measured" signal) — hero only. The faint inner-loop
                coral rupture seam was removed (doubled coral artifact); the single precise
                coral rim stroke is now the only coral loop signal. */}
            {withRadar && (
              <g className="af-contours">
                <g stroke={C.ink2} strokeOpacity="0.20" strokeWidth="1" vectorEffect="non-scaling-stroke">
                  <line x1="988" y1="536" x2="996" y2="536" />
                  <line x1="992" y1="532" x2="992" y2="540" />
                  <line x1="736" y1="224" x2="744" y2="224" />
                  <line x1="740" y1="220" x2="740" y2="228" />
                </g>
              </g>
            )}
          </g>
        </g>
      </g>
    </svg>
  );
}

// Re-export the canonical domain names for consumers (e.g. future tooltips/a11y).
export const ANALYTICAL_DOMAINS = DOMAIN_FULL;